import React, { useState, useEffect, useRef } from 'react';
import {
 Box,
 Card,
 CardContent,
 Typography,
 Button,
 TextField,
 Avatar,
 Badge,
 List,
 ListItem,
 ListItemText,
 ListItemAvatar,
 IconButton,
 Dialog,
 DialogTitle,
 DialogContent,
 DialogActions,
 Chip,
 Paper,
 Divider,
 Tooltip,
 Menu,
 MenuItem,
 InputAdornment
} from '@mui/material';
import {
 Send,
 AttachFile,
 EmojiEmotions,
 MoreVert,
 Group,
 Person,
 VideoCall,
 Phone,
 Search,
 FilterList,
 Notifications,
 NotificationsOff,
 Settings,
 Archive,
 Block
} from '@mui/icons-material';
import './TeamChatRoom.css';

const TeamChatRoom = ({ 
 teamMembers = [], 
 projectTitle ="Team Project",
 onClose,
 open = false 
}) => {
 const [messages, setMessages] = useState([]);
 const [newMessage, setNewMessage] = useState('');
 const [activeUsers, setActiveUsers] = useState(new Set());
 const [selectedUser, setSelectedUser] = useState(null);
 const [searchTerm, setSearchTerm] = useState('');
 const [filteredMessages, setFilteredMessages] = useState([]);
 const [notificationsEnabled, setNotificationsEnabled] = useState(true);
 const [showEmojiPicker, setShowEmojiPicker] = useState(false);
 const [anchorEl, setAnchorEl] = useState(null);
 const messagesEndRef = useRef(null);

 // Mock messages data
 const mockMessages = [
 {
 id: 1,
 senderId: 'person1',
 senderName: 'Alex Johnson',
 message: 'Hey team! I\'ve completed the authentication design. The wireframes are ready for review.',
 timestamp: new Date(Date.now() - 1000 * 60 * 30),
 type: 'text',
 reactions: []
 },
 {
 id: 2,
 senderId: 'person2',
 senderName: 'Sarah Chen',
 message: 'Great work Alex! I\'m working on the backend APIs now. Should be ready by EOD.',
 timestamp: new Date(Date.now() - 1000 * 60 * 25),
 type: 'text',
 reactions: [{ emoji: '👍', count: 2, users: ['person1', 'person3'] }]
 },
 {
 id: 3,
 senderId: 'person4',
 senderName: 'Emily Watson',
 message: 'I can start testing the auth flow once Sarah completes the APIs. What\'s the timeline?',
 timestamp: new Date(Date.now() - 1000 * 60 * 20),
 type: 'text',
 reactions: []
 },
 {
 id: 4,
 senderId: 'person3',
 senderName: 'Mike Rodriguez',
 message: 'I\'ll need the API endpoints to start building the frontend components. Can you share the API docs?',
 timestamp: new Date(Date.now() - 1000 * 60 * 15),
 type: 'text',
 reactions: []
 },
 {
 id: 5,
 senderId: 'person2',
 senderName: 'Sarah Chen',
 message: 'Sure! I\'ll create the API documentation and share it in the next hour.',
 timestamp: new Date(Date.now() - 1000 * 60 * 10),
 type: 'text',
 reactions: [{ emoji: '🎉', count: 1, users: ['person3'] }]
 },
 {
 id: 6,
 senderId: 'person1',
 senderName: 'Alex Johnson',
 message: 'Perfect! Here\'s the link to the design files: https://figma.com/design/abc123',
 timestamp: new Date(Date.now() - 1000 * 60 * 5),
 type: 'link',
 reactions: [{ emoji: '👀', count: 3, users: ['person2', 'person3', 'person4'] }]
 }
 ];

 // Mock team members
 const defaultTeamMembers = [
 {
 id: 'person1',
 name: 'Alex Johnson',
 role: 'Frontend Developer',
 avatar: 'A',
 color: '#4CAF50',
 status: 'online',
 lastSeen: new Date()
 },
 {
 id: 'person2',
 name: 'Sarah Chen',
 role: 'Backend Developer',
 avatar: 'S',
 color: '#2196F3',
 status: 'online',
 lastSeen: new Date()
 },
 {
 id: 'person3',
 name: 'Mike Rodriguez',
 role: 'UI/UX Developer',
 avatar: 'M',
 color: '#FF9800',
 status: 'away',
 lastSeen: new Date(Date.now() - 1000 * 60 * 15)
 },
 {
 id: 'person4',
 name: 'Emily Watson',
 role: 'QA/Testing Engineer',
 avatar: 'E',
 color: '#9C27B0',
 status: 'online',
 lastSeen: new Date()
 }
 ];

 const currentTeamMembers = teamMembers.length > 0 ? teamMembers : defaultTeamMembers;

 useEffect(() => {
 setMessages(mockMessages);
 setActiveUsers(new Set(currentTeamMembers.filter(m => m.status === 'online').map(m => m.id)));
 }, [teamMembers]);

 useEffect(() => {
 if (searchTerm) {
 const filtered = messages.filter(msg => 
 msg.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
 msg.senderName.toLowerCase().includes(searchTerm.toLowerCase())
 );
 setFilteredMessages(filtered);
 } else {
 setFilteredMessages(messages);
 }
 }, [messages, searchTerm]);

 useEffect(() => {
 scrollToBottom();
 }, [messages]);

 const scrollToBottom = () => {
 messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
 };

 const handleSendMessage = () => {
 if (newMessage.trim()) {
 const message = {
 id: messages.length + 1,
 senderId: 'person1', // Current user - in real app, this would be dynamic
 senderName: 'Alex Johnson',
 message: newMessage.trim(),
 timestamp: new Date(),
 type: 'text',
 reactions: []
 };
 setMessages(prev => [...prev, message]);
 setNewMessage('');
 }
 };

 const handleKeyPress = (e) => {
 if (e.key === 'Enter' && !e.shiftKey) {
 e.preventDefault();
 handleSendMessage();
 }
 };

 const formatTime = (timestamp) => {
 const now = new Date();
 const diff = now - timestamp;
 const minutes = Math.floor(diff / (1000 * 60));
 const hours = Math.floor(diff / (1000 * 60 * 60));
 const days = Math.floor(diff / (1000 * 60 * 60 * 24));

 if (minutes < 1) return 'Just now';
 if (minutes < 60) return `${minutes}m ago`;
 if (hours < 24) return `${hours}h ago`;
 if (days < 7) return `${days}d ago`;
 return timestamp.toLocaleDateString();
 };

 const handleReaction = (messageId, emoji) => {
 setMessages(prev => prev.map(msg => {
 if (msg.id === messageId) {
 const existingReaction = msg.reactions.find(r => r.emoji === emoji);
 if (existingReaction) {
 if (existingReaction.users.includes('person1')) {
 // Remove reaction
 existingReaction.users = existingReaction.users.filter(u => u !== 'person1');
 existingReaction.count--;
 if (existingReaction.count === 0) {
 msg.reactions = msg.reactions.filter(r => r.emoji !== emoji);
 }
 } else {
 // Add reaction
 existingReaction.users.push('person1');
 existingReaction.count++;
 }
 } else {
 // New reaction
 msg.reactions.push({
 emoji,
 count: 1,
 users: ['person1']
 });
 }
 }
 return msg;
 }));
 };

 const getStatusColor = (status) => {
 switch (status) {
 case 'online': return '#4CAF50';
 case 'away': return '#FF9800';
 case 'offline': return '#9E9E9E';
 default: return '#9E9E9E';
 }
 };

 const getMessageTypeIcon = (type) => {
 switch (type) {
 case 'link': return '🔗';
 case 'file': return '📎';
 case 'image': return '🖼️';
 default: return '';
 }
 };

 const handleMenuClick = (event) => {
 setAnchorEl(event.currentTarget);
 };

 const handleMenuClose = () => {
 setAnchorEl(null);
 };

 const displayMessages = searchTerm ? filteredMessages : messages;

 return (
 <Dialog 
 open={open} 
 onClose={onClose}
 maxWidth="lg"
 fullWidth
 PaperProps={{
 sx: { height: '80vh', maxHeight: '800px' }
 }}
 >
 <DialogTitle sx={{ pb: 1 }}>
 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
 <Box sx={{ display: 'flex', alignItems: 'center' }}>
 <Group sx={{ mr: 1, color: '#2196F3' }} />
 <Box>
 <Typography variant="h6" component="div">
 {projectTitle} - Team Chat
 </Typography>
 <Typography variant="body2" color="text.secondary">
 {activeUsers.size} members online
 </Typography>
 </Box>
 </Box>
 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
 <IconButton size="small">
 <VideoCall />
 </IconButton>
 <IconButton size="small">
 <Phone />
 </IconButton>
 <IconButton 
 size="small"
 onClick={handleMenuClick}
 >
 <MoreVert />
 </IconButton>
 <Menu
 anchorEl={anchorEl}
 open={Boolean(anchorEl)}
 onClose={handleMenuClose}
 >
 <MenuItem onClick={() => setNotificationsEnabled(!notificationsEnabled)}>
 {notificationsEnabled ? <NotificationsOff /> : <Notifications />}
 {notificationsEnabled ? 'Disable' : 'Enable'} Notifications
 </MenuItem>
 <MenuItem>
 <Archive />
 Archive Chat
 </MenuItem>
 <MenuItem>
 <Settings />
 Chat Settings
 </MenuItem>
 </Menu>
 </Box>
 </Box>
 </DialogTitle>

 <DialogContent sx={{ p: 0, display: 'flex', height: '100%' }}>
 <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
 {/* Chat Messages */}
 <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
 {/* Search Bar */}
 <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
 <TextField
 fullWidth
 size="small"
 placeholder="Search messages..."
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 InputProps={{
 startAdornment: (
 <InputAdornment position="start">
 <Search />
 </InputAdornment>
 ),
 endAdornment: searchTerm && (
 <InputAdornment position="end">
 <IconButton size="small" onClick={() => setSearchTerm('')}>
 ×
 </IconButton>
 </InputAdornment>
 )
 }}
 />
 </Box>

 {/* Messages */}
 <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
 {displayMessages.map((message) => {
 const sender = currentTeamMembers.find(m => m.id === message.senderId);
 const isCurrentUser = message.senderId === 'person1';
 
 return (
 <Box key={message.id} sx={{ mb: 2 }}>
 <Box sx={{ 
 display: 'flex', 
 alignItems: 'flex-start',
 justifyContent: isCurrentUser ? 'flex-end' : 'flex-start'
 }}>
 {!isCurrentUser && (
 <Avatar 
 sx={{ 
 bgcolor: sender?.color || '#9E9E9E',
 width: 32,
 height: 32,
 mr: 1,
 mt: 0.5
 }}
 >
 {sender?.avatar || '?'}
 </Avatar>
 )}
 
 <Box sx={{ 
 maxWidth: '70%',
 display: 'flex',
 flexDirection: 'column',
 alignItems: isCurrentUser ? 'flex-end' : 'flex-start'
 }}>
 {!isCurrentUser && (
 <Typography variant="caption" sx={{ mb: 0.5, color: 'text.secondary' }}>
 {message.senderName} • {formatTime(message.timestamp)}
 </Typography>
 )}
 
 <Paper sx={{
 p: 1.5,
 borderRadius: 2,
 bgcolor: isCurrentUser ? '#2196F3' : 'grey.100',
 color: isCurrentUser ? 'white' : 'text.primary',
 position: 'relative'
 }}>
 <Typography variant="body2">
 {getMessageTypeIcon(message.type)} {message.message}
 </Typography>
 </Paper>
 
 {message.reactions.length > 0 && (
 <Box sx={{ 
 display: 'flex', 
 gap: 0.5, 
 mt: 0.5,
 flexWrap: 'wrap'
 }}>
 {message.reactions.map((reaction, index) => (
 <Chip
 key={index}
 label={`${reaction.emoji} ${reaction.count}`}
 size="small"
 variant="outlined"
 sx={{ 
 height: 24,
 fontSize: '0.75rem',
 cursor: 'pointer',
 '&:hover': { bgcolor: 'action.hover' }
 }}
 onClick={() => handleReaction(message.id, reaction.emoji)}
 />
 ))}
 </Box>
 )}
 
 {isCurrentUser && (
 <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
 {formatTime(message.timestamp)}
 </Typography>
 )}
 </Box>
 
 {isCurrentUser && (
 <Avatar 
 sx={{ 
 bgcolor: '#2196F3',
 width: 32,
 height: 32,
 ml: 1,
 mt: 0.5
 }}
 >
 A
 </Avatar>
 )}
 </Box>
 </Box>
 );
 })}
 <div ref={messagesEndRef} />
 </Box>

 {/* Message Input */}
 <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
 <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
 <IconButton size="small">
 <AttachFile />
 </IconButton>
 
 <TextField
 fullWidth
 multiline
 maxRows={4}
 placeholder="Type your message..."
 value={newMessage}
 onChange={(e) => setNewMessage(e.target.value)}
 onKeyPress={handleKeyPress}
 InputProps={{
 endAdornment: (
 <InputAdornment position="end">
 <IconButton 
 size="small"
 onClick={() => setShowEmojiPicker(!showEmojiPicker)}
 >
 <EmojiEmotions />
 </IconButton>
 </InputAdornment>
 )
 }}
 />
 
 <Button 
 variant="contained" 
 onClick={handleSendMessage}
 disabled={!newMessage.trim()}
 sx={{ minWidth: 'auto', px: 2 }}
 >
 <Send />
 </Button>
 </Box>
 </Box>
 </Box>

 {/* Team Members Sidebar */}
 <Box sx={{ 
 width: 250, 
 borderLeft: 1, 
 borderColor: 'divider',
 display: 'flex',
 flexDirection: 'column'
 }}>
 <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
 <Typography variant="subtitle2" color="text.secondary">
 Team Members ({currentTeamMembers.length})
 </Typography>
 </Box>
 
 <Box sx={{ flex: 1, overflow: 'auto' }}>
 {currentTeamMembers.map((member) => (
 <ListItem 
 key={member.id} 
 sx={{ 
 cursor: 'pointer',
 '&:hover': { bgcolor: 'action.hover' }
 }}
 onClick={() => setSelectedUser(member)}
 >
 <ListItemAvatar>
 <Badge
 overlap="circular"
 anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
 badgeContent={
 <Box
 sx={{
 width: 8,
 height: 8,
 borderRadius: '50%',
 bgcolor: getStatusColor(member.status),
 border: '2px solid white'
 }}
 />
 }
 >
 <Avatar sx={{ bgcolor: member.color }}>
 {member.avatar}
 </Avatar>
 </Badge>
 </ListItemAvatar>
 <ListItemText
 primary={member.name}
 secondary={
 <Box>
 <Typography variant="caption" color="text.secondary">
 {member.role}
 </Typography>
 <Typography variant="caption" display="block" color="text.secondary">
 {member.status === 'online' ? 'Online' : 
 member.status === 'away' ? 'Away' : 'Offline'}
 </Typography>
 </Box>
 }
 />
 </ListItem>
 ))}
 </Box>
 </Box>
 </Box>
 </DialogContent>

 <DialogActions>
 <Button onClick={onClose}>Close</Button>
 </DialogActions>
 </Dialog>
 );
};

export default TeamChatRoom;




